<template>
  <div class="mazda-page">
    <MazdaPageHeader
      :server-name="radio1"
      :server-tone-class="serverToneClass"
      :server-env-desc="serverEnvDesc"
      @update:server-name="radio1 = $event"
    />

    <MazdaHomeDashboard
      v-if="!activePanel"
      :server-name="radio1"
      :server-mode-label="serverModeLabel"
      :allowed-panels="allowedPanelKeys"
      :limited-access="!hasMazdaPermission"
      @open-panel="openPanel"
    />

    <div v-else class="panel-workspace">
      <MazdaWorkspaceHeader
        :current-panel-title="currentPanelTitle"
        :current-panel-desc="currentPanelDesc"
        :server-mode-label="serverModeLabel"
        :server-tag-type="serverTagType"
        :server-name="radio1"
        @go-home="goHome"
      />

      <component
        v-if="currentPanelComponent"
        :is="currentPanelComponent"
        :key="activePanel"
        :server-name="radio1"
        :mazda-post="mazdaPost"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import store from '@/store';

import MazdaPageHeader from '@/components/mazda/MazdaPageHeader.vue';
import MazdaHomeDashboard from '@/components/mazda/MazdaHomeDashboard.vue';
import MazdaWorkspaceHeader from '@/components/mazda/MazdaWorkspaceHeader.vue';

import MazdaReplacePanel from '@/components/mazda/panels/MazdaReplacePanel.vue';
import MazdaAbPanel from '@/components/mazda/panels/MazdaAbPanel.vue';
import MazdaDataOpsPanel from '@/components/mazda/panels/MazdaDataOpsPanel.vue';
import MazdaSoVersionPanel from '@/components/mazda/panels/MazdaSoVersionPanel.vue';
import MazdaAccountSettingsPanel from '@/components/mazda/panels/MazdaAccountSettingsPanel.vue';
import MazdaInterfaceDocsPanel from '@/components/mazda/panels/MazdaInterfaceDocsPanel.vue';
import MazdaCrawlerBoxPanel from '@/components/mazda/panels/MazdaCrawlerBoxPanel.vue';
import MazdaSm2KeysPanel from '@/components/mazda/panels/MazdaSm2KeysPanel.vue';

import { useMazdaServer } from '@/composables/mazda/useMazdaServer';

/**
 * 只在 MAZDA 页面过滤 ResizeObserver loop 的开发环境误报。
 * 不替换 window.ResizeObserver，避免 Element Plus Select 在面板切换卸载后延迟测量时报空。
 */

const resizeObserverErrorMessages = [
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications.',
];

const handleMazdaResizeObserverError = (event) => {
  if (resizeObserverErrorMessages.includes(event.message)) {
    event.stopImmediatePropagation();
    event.preventDefault();
    return false;
  }

  return undefined;
};

const handleMazdaResizeObserverReject = (event) => {
  const message = event?.reason?.message || event?.reason || '';

  if (resizeObserverErrorMessages.includes(message)) {
    event.stopImmediatePropagation();
    event.preventDefault();
    return false;
  }

  return undefined;
};

onMounted(() => {
  window.addEventListener('error', handleMazdaResizeObserverError, true);
  window.addEventListener('unhandledrejection', handleMazdaResizeObserverReject, true);
});

onBeforeUnmount(() => {
  window.removeEventListener('error', handleMazdaResizeObserverError, true);
  window.removeEventListener('unhandledrejection', handleMazdaResizeObserverReject, true);
});

const activePanel = ref('');
const LIMITED_PANEL_KEYS = ['interfaceDocs', 'crawlerBox'];

const {
  radio1,
  serverModeLabel,
  serverEnvDesc,
  serverTagType,
  serverToneClass,
  mazdaPost,
} = useMazdaServer();

const panelMeta = {
  replace: {
    title: '上传文件',
    desc: '上传单个文件、校验目标路径并查看目标文件当前大小。',
    component: MazdaReplacePanel,
  },
  ab: {
    title: 'AB 数据上传与查询',
    desc: '上传 .ab 文件并查看 SQL 中已存在的数据列表。',
    component: MazdaAbPanel,
  },
  dataOps: {
    title: '数据维护',
    desc: '集中处理 Redis 导入、Redis 查询、日志及其他数据、车型数据收集和测试接头。',
    component: MazdaDataOpsPanel,
  },
  soVersion: {
    title: '统一项 / SO 版本管理',
    desc: '维护 MAZDA 正式版本、测试版本和 SO 版本列表。',
    component: MazdaSoVersionPanel,
  },
  accountSettings: {
    title: '马自达账号 / Cookie / GGP 管理',
    desc: '维护马自达账号密码、在线 Cookie 和 GGP 版本。',
    component: MazdaAccountSettingsPanel,
  },
  interfaceDocs: {
    title: '接口说明',
    desc: '维护 tool_MAZDA 功能号说明、启用版本和弃用状态。',
    component: MazdaInterfaceDocsPanel,
  },
  crawlerBox: {
    title: '测试接头处理',
    desc: '维护 MAZDA 测试接头开关状态。',
    component: MazdaCrawlerBoxPanel,
  },
  sm2Keys: {
    title: 'SM2 密钥管理',
    desc: '查看 SM2 公钥、私钥、生成时间，并新增同步到双服务器的密钥组。',
    component: MazdaSm2KeysPanel,
  },
};

panelMeta.dataOps.desc = '集中处理 Redis 导入、Redis 查询、日志及其他数据、车型数据收集。';

const isMazdaTemplate = (value) => String(value || '').trim().toUpperCase() === 'MAZDA';

const userData = computed(() => store.state.userData || {});

const pickUsername = (data) => {
  if (!data || typeof data !== 'object') {
    return '';
  }

  const candidates = [
    data.username,
    data.userName,
    data.UserName,
    data.loginName,
    data.LoginName,
    data.name,
    data.account,
  ];

  for (const item of candidates) {
    const value = String(item || '').trim();
    if (value && value !== 'hahaha') {
      return value;
    }
  }

  if (data.userInfo) {
    return pickUsername(data.userInfo);
  }
  if (data.data) {
    return pickUsername(data.data);
  }

  return '';
};

const isLuocong = computed(() => pickUsername(userData.value) === 'luocong');

const hasMazdaPermission = computed(() => {
  const templates = userData.value?.permissionTemplates;
  if (Array.isArray(templates)) {
    return templates.some(isMazdaTemplate);
  }
  return isMazdaTemplate(userData.value?.permissionTemplate);
});

const allowedPanelKeys = computed(() => {
  if (!hasMazdaPermission.value) {
    return LIMITED_PANEL_KEYS;
  }

  return Object.keys(panelMeta).filter((panel) => panel !== 'sm2Keys' || isLuocong.value);
});

const isPanelAllowed = (panel) => allowedPanelKeys.value.includes(panel);

const currentPanelTitle = computed(() => (isPanelAllowed(activePanel.value) ? panelMeta[activePanel.value]?.title || '' : ''));
const currentPanelDesc = computed(() => (isPanelAllowed(activePanel.value) ? panelMeta[activePanel.value]?.desc || '' : ''));
const currentPanelComponent = computed(() => (isPanelAllowed(activePanel.value) ? panelMeta[activePanel.value]?.component || null : null));

const openPanel = (panel) => {
  if (!isPanelAllowed(panel)) {
    activePanel.value = '';
    return;
  }
  activePanel.value = panel;
};

const goHome = () => {
  activePanel.value = '';
};

watch(allowedPanelKeys, () => {
  if (activePanel.value && !isPanelAllowed(activePanel.value)) {
    activePanel.value = '';
  }
});
</script>

<style scoped>
.mazda-page {
  min-height: 100%;
  min-width: 0;
  padding: 16px 20px 28px;
  box-sizing: border-box;
  overflow-x: hidden;
  background: linear-gradient(180deg, #f7f9fc 0%, #eef3f9 100%);
}

.panel-workspace {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  width: 100%;
  max-width: 100%;
}

.mazda-page :deep(*) {
  box-sizing: border-box;
}

.mazda-page :deep(.el-card),
.mazda-page :deep(.el-form),
.mazda-page :deep(.el-row),
.mazda-page :deep(.el-col),
.mazda-page :deep(.el-tabs),
.mazda-page :deep(.el-tabs__content),
.mazda-page :deep(.el-tab-pane),
.mazda-page :deep(.el-table),
.mazda-page :deep(.el-table__inner-wrapper),
.mazda-page :deep(.el-table__body-wrapper),
.mazda-page :deep(.el-scrollbar),
.mazda-page :deep(.el-scrollbar__wrap) {
  min-width: 0;
  max-width: 100%;
}

.mazda-page :deep(.el-table) {
  width: 100%;
  border-radius: 10px;
  overflow: hidden;
}

.mazda-page :deep(.el-table__body-wrapper) {
  overflow-x: auto;
}

.mazda-page :deep(.el-scrollbar__wrap) {
  overflow-x: auto;
}

.mazda-page :deep(.el-button) {
  height: 36px;
  padding: 0 14px;
  border-radius: 8px;
  font-weight: 700;
  letter-spacing: 0;
  transition:
    transform 0.18s ease,
    box-shadow 0.18s ease,
    border-color 0.18s ease,
    background-color 0.18s ease;
}

.mazda-page :deep(.el-button + .el-button) {
  margin-left: 0;
}

.mazda-page :deep(.el-button:hover) {
  transform: translateY(-1px);
}

.mazda-page :deep(.el-button--default),
.mazda-page :deep(.el-button.is-plain) {
  background: linear-gradient(180deg, #ffffff, #f7faff);
  border-color: #dbe5f2;
  color: #4b5565;
  box-shadow: 0 6px 16px rgba(31, 56, 88, 0.06);
}

.mazda-page :deep(.el-button--default:hover),
.mazda-page :deep(.el-button.is-plain:hover) {
  border-color: #bfd0e6;
  color: #2f3a4a;
  background: linear-gradient(180deg, #ffffff, #f2f7ff);
  box-shadow: 0 10px 20px rgba(31, 56, 88, 0.09);
}

.mazda-page :deep(.el-button--default:active),
.mazda-page :deep(.el-button.is-plain:active) {
  transform: translateY(0);
}

.mazda-page :deep(.el-button--primary) {
  border: none;
  background: linear-gradient(135deg, #4f9cff 0%, #3a8cff 100%);
  box-shadow: 0 8px 20px rgba(64, 158, 255, 0.18);
}

.mazda-page :deep(.el-button--primary:hover) {
  background: linear-gradient(135deg, #66a8ff 0%, #4a94ff 100%);
  box-shadow: 0 14px 24px rgba(64, 158, 255, 0.28);
}

.mazda-page :deep(.el-button--success) {
  border: none;
  background: linear-gradient(135deg, #67c23a 0%, #4fb12e 100%);
  box-shadow: 0 8px 20px rgba(103, 194, 58, 0.18);
}

.mazda-page :deep(.el-button--warning) {
  border: none;
  background: linear-gradient(135deg, #e6a23c 0%, #d99121 100%);
  box-shadow: 0 8px 20px rgba(230, 162, 60, 0.18);
}

.mazda-page :deep(.el-button--danger) {
  border: none;
  background: linear-gradient(135deg, #f56c6c 0%, #e75252 100%);
  box-shadow: 0 8px 20px rgba(245, 108, 108, 0.18);
}

.mazda-page :deep(.el-button--primary.is-disabled),
.mazda-page :deep(.el-button--success.is-disabled),
.mazda-page :deep(.el-button--warning.is-disabled),
.mazda-page :deep(.el-button--danger.is-disabled),
.mazda-page :deep(.el-button--default.is-disabled),
.mazda-page :deep(.el-button.is-disabled) {
  transform: none;
  box-shadow: none;
}

.mazda-page :deep(.el-tag) {
  height: 28px;
  padding: 0 10px;
  border-radius: 8px;
  font-weight: 700;
}

.mazda-page :deep(.el-tag--info.el-tag--plain) {
  background: linear-gradient(180deg, #ffffff, #f7faff);
  border-color: #dbe5f2;
  color: #5f6b7a;
}

.mazda-page :deep(.el-tag--primary),
.mazda-page :deep(.el-tag--success),
.mazda-page :deep(.el-tag--warning),
.mazda-page :deep(.el-tag--danger),
.mazda-page :deep(.el-tag--info) {
  box-shadow: 0 6px 14px rgba(31, 56, 88, 0.06);
}

.mazda-page :deep(.el-upload-dragger) {
  border-radius: 12px;
  border-color: #dbe5f2;
  background: linear-gradient(180deg, #fbfdff, #f6faff);
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.2s ease;
}

.mazda-page :deep(.el-upload-dragger:hover) {
  border-color: #8bb8ff;
  box-shadow: 0 12px 26px rgba(64, 158, 255, 0.1);
}

.mazda-page :deep(.el-input__wrapper),
.mazda-page :deep(.el-select__wrapper) {
  min-height: 38px;
  border-radius: 10px;
  box-shadow:
    0 1px 2px rgba(16, 24, 40, 0.04),
    0 0 0 1px rgba(219, 229, 242, 0.88) inset;
  transition:
    box-shadow 0.18s ease,
    transform 0.18s ease;
}

.mazda-page :deep(.el-input__wrapper:hover),
.mazda-page :deep(.el-select__wrapper:hover),
.mazda-page :deep(.el-textarea__inner:hover) {
  box-shadow:
    0 1px 2px rgba(16, 24, 40, 0.06),
    0 0 0 1px rgba(160, 190, 228, 0.96) inset;
}

.mazda-page :deep(.el-input__wrapper.is-focus),
.mazda-page :deep(.el-select__wrapper.is-focused),
.mazda-page :deep(.el-textarea__inner:focus) {
  box-shadow:
    0 0 0 3px rgba(64, 158, 255, 0.14),
    0 0 0 1px rgba(64, 158, 255, 0.92) inset;
}

.mazda-page :deep(.el-textarea__inner) {
  border-radius: 10px;
  transition:
    box-shadow 0.18s ease,
    border-color 0.18s ease;
}

.mazda-page :deep(.el-card) {
  background: rgba(255, 255, 255, 0.94);
}

.mazda-page :deep(.el-card__body) {
  padding: 14px;
}

.mazda-page :deep(.el-upload-list) {
  width: 100%;
  margin-top: 12px;
}

.mazda-page :deep(.el-table th.el-table__cell) {
  background: #f7faff;
  color: #5d6678;
  font-weight: 700;
}

.mazda-page :deep(.el-dialog) {
  border-radius: 14px;
}

.mazda-page :deep(.el-dialog__body) {
  min-width: 0;
}

@media (max-width: 768px) {
  .mazda-page {
    padding: 14px;
  }

  .mazda-page :deep(.el-card__body) {
    padding: 12px;
  }

  .mazda-page :deep(.el-button) {
    height: 34px;
    padding: 0 12px;
  }
}
</style>
